import { useState } from 'react';
import { Button, IconButton, Tooltip, CircularProgress } from '@material-ui/core';
import { useApi, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { usePermission } from '@backstage/plugin-permission-react';
import type { ResourcePermission } from '@backstage/plugin-permission-common';

interface Props {
  permission: ResourcePermission;
  label: string;
  // Resource the action targets — passed through to usePermission as
  // resourceRef so the policy can do per-resource gating later. For
  // workflows we use `<namespace>/<name>`.
  resourceRef: string;
  // Path on the Backstage backend, e.g. "/api/argo-actions/argo/retry/ci/foo".
  backendPath: string;
  onSuccess?: (msg: string) => void;
  onError?: (msg: string) => void;
  variant?: 'outlined' | 'contained' | 'text';
  color?: 'primary' | 'secondary' | 'default';
  icon?: React.ReactElement;
}

export function PermissionedAction({
  permission,
  label,
  resourceRef,
  backendPath,
  onSuccess,
  onError,
  variant = 'outlined',
  color,
  icon,
}: Props) {
  const { allowed, loading } = usePermission({
    permission,
    resourceRef,
  });
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [busy, setBusy] = useState(false);

  if (!loading && !allowed) return null;

  const click = async () => {
    setBusy(true);
    try {
      const base = await discoveryApi.getBaseUrl('argo-actions');
      const suffix = backendPath.replace(/^\/api\/argo-actions/, '');
      const res = await fetchApi.fetch(`${base}${suffix}`, { method: 'POST' });
      if (res.status === 403) {
        onError?.('Forbidden — your group is not allowed to do that.');
      } else if (!res.ok) {
        const body = await res.text().catch(() => '');
        onError?.(`Failed: ${res.status} ${res.statusText} — ${body.slice(0, 200)}`);
      } else {
        onSuccess?.(`${label} OK`);
      }
    } catch (e: any) {
      onError?.(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const tooltipTitle = loading
    ? 'Checking permission…'
    : `Run ${label} as the configured Argo service account`;

  if (icon) {
    return (
      <Tooltip title={tooltipTitle}>
        <span>
          <IconButton
            size="small"
            onClick={click}
            disabled={loading || busy}
          >
            {busy ? <CircularProgress size={16} /> : icon}
          </IconButton>
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={tooltipTitle}>
      <span>
        <Button
          size="small"
          variant={variant}
          color={color}
          onClick={click}
          disabled={loading || busy}
          startIcon={busy ? <CircularProgress size={14} /> : undefined}
        >
          {label}
        </Button>
      </span>
    </Tooltip>
  );
}
